/**
 * ═══════════════════════════════════════════════════════════════════════
 * Childhood Horizons — Central Assets Library
 * ═══════════════════════════════════════════════════════════════════════
 *
 * All visual imagery used across the site MUST be sourced from this file.
 * Rules:
 *  1. Only original imagery generated for Childhood Horizons — no stock,
 *     no external CDN, no watermarked or copyrighted material.
 *  2. New images are added to `/app/frontend/src/assets/library/` and
 *     exported below with a semantic name.
 *  3. Every component / page imports images through `libraryImages` — never
 *     inlines a URL.
 *  4. Whenever a new page or service is introduced, pick the best-matching
 *     asset from this library. Only add new ones when nothing in the
 *     existing library fits the section's intent.
 *
 * All assets below are:
 *  ✔ Web-optimised (max 1600px wide, JPEG q82, progressive).
 *  ✔ Bundled via webpack — served same-origin, cache-busted per deploy.
 *  ✔ Free of logos, text overlays, watermarks, and copyright encumbrance.
 * ═══════════════════════════════════════════════════════════════════════
 */

// Saudi-authentic classroom + interior scenes (highest-quality originals).
import saudiClassroomHero from './library/saudi-classroom-hero.jpeg';
import saudiClassroomActivities from './library/saudi-classroom-activities.jpeg';
import saudiMontessoriSensorial from './library/saudi-montessori-sensorial.jpeg';
import saudiNatureLearningCorners from './library/saudi-nature-learning-corners.jpeg';
import saudiOutdoorClimbing from './library/saudi-outdoor-climbing.jpeg';
import saudiLightTablePlay from './library/saudi-light-table-play.jpeg';
import kindergartenCorridor from './library/kindergarten-corridor.jpeg';

// Pedagogical documentation walls.
import documentationPlantsAr from './library/documentation-plants-ar.jpeg';
import natureExplorersWall from './library/nature-explorers-wall.jpeg';
import documentationCollage from './library/documentation-collage.jpeg';
import documentationPlants from './library/documentation-plants.jpeg';

// Classroom / process moments.
import artColorExploration from './library/art-color-exploration.jpeg';
import classAgreements from './library/class-agreements.jpeg';
import inquiryNatureCorner from './library/inquiry-nature-corner.jpeg';
import leavesInquiry from './library/leaves-inquiry.jpeg';
import mudKitchenOutdoor from './library/mud-kitchen-outdoor.jpeg';
import dailyPlanBoard from './library/daily-plan-board.jpeg';

// Premium services scenes (institutional / high-value engagements).
import premiumChildExperience from './library/premium-child-experience.jpeg';
import premiumMysteryParent from './library/premium-mystery-parent.jpeg';
import premiumPreopeningReadiness from './library/premium-preopening-readiness.jpeg';
import premiumOperationalAudit from './library/premium-operational-audit.jpeg';
import premiumStrategicAdvisory from './library/premium-strategic-advisory.jpeg';

// Semantic export — always import by name, never by file path.
export const libraryImages = {
  // ── Saudi flagship scenes (use for Hero / About / premium interiors) ─
  saudiClassroomHero,          // Saudi Montessori classroom — flagship Hero image
  saudiClassroomActivities,    // Saudi Montessori — reading + drawing activities
  saudiMontessoriSensorial,    // Saudi Montessori pink tower + brown stair sensorial
  saudiNatureLearningCorners,  // Saudi kids exploring nature with magnifying glasses
  saudiOutdoorClimbing,        // Saudi outdoor pergola garden play
  saudiLightTablePlay,         // Saudi light table sensorial exploration
  kindergartenCorridor,        // Premium kindergarten interior corridor (branded)

  // ── Pedagogical documentation ────────────────────────────────────────
  documentationPlantsAr,       // Arabic plant-inquiry documentation wall (rich)
  natureExplorersWall,         // Bilingual "little nature explorers" wall
  documentationCollage,        // Documentation methodology collage
  documentationPlants,         // Botanical inquiry documentation

  // ── Classroom / process moments ──────────────────────────────────────
  artColorExploration,         // fine-motor / creative process
  classAgreements,             // policies-procedures / classroom governance
  inquiryNatureCorner,         // inquiry-based learning corner
  leavesInquiry,               // teacher + children inquiry (leaves)
  mudKitchenOutdoor,           // outdoor / mud kitchen / play-based
  dailyPlanBoard,              // Saudi daily-plan wall — structured routines / policies

  // ── Premium services ──────────────────────────────────────────────────
  premiumChildExperience,      // "كل طفل فنان" — child voice / experience audit
  premiumMysteryParent,        // teacher + children reading circle — parent-experience lens
  premiumPreopeningReadiness,  // outdoor playground — pre-opening readiness
  premiumOperationalAudit,     // full classroom overview — operational audit
  premiumStrategicAdvisory,    // collaborative future-city project — strategic advisory
};

export default libraryImages;
